import { useEffect, useState } from "react";
import { Plus, Trash2, Star, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function AdminTestimonialsPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ author: "", rating: 5, content: "", location: "" });
    const [error, setError] = useState("");

    const load = () => {
        setLoading(true);
        api.get("/admin/testimonials").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        try {
            await api.post("/admin/testimonials", { ...form, rating: Number(form.rating) });
            setShowForm(false);
            setForm({ author: "", rating: 5, content: "", location: "" });
            load();
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || err.message);
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Изтриване на отзива?")) return;
        await api.delete(`/admin/testimonials/${id}`);
        load();
    };

    return (
        <div data-testid="admin-testimonials-page">
            <div className="flex items-center justify-between mb-10">
                <div>
                    <h1 className="font-display text-4xl tracking-tightest">Отзиви</h1>
                    <p className="text-ink-muted mt-1">Управление на потребителските отзиви</p>
                </div>
                <button
                    onClick={() => setShowForm(true)}
                    className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-bg px-5 py-3 rounded-full font-medium"
                    data-testid="admin-new-testimonial"
                >
                    <Plus size={16} /> Нов отзив
                </button>
            </div>

            {loading ? <div className="text-ink-muted">Зареждане…</div> : (
                <div className="grid md:grid-cols-2 gap-5">
                    {items.map((t) => (
                        <div key={t.id} className="bg-surface border border-line rounded-2xl p-6" data-testid={`testimonial-${t.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="font-medium">{t.author}</div>
                                    {t.location && <div className="text-xs text-ink-subtle mt-1">{t.location}</div>}
                                </div>
                                <button
                                    onClick={() => onDelete(t.id)}
                                    className="w-8 h-8 rounded-lg border border-line hover:border-red-500 hover:text-red-400 flex items-center justify-center"
                                    data-testid={`testimonial-delete-${t.id}`}
                                >
                                    <Trash2 size={13} />
                                </button>
                            </div>
                            <div className="flex gap-1 mt-3">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} size={14} className={i < t.rating ? "text-gold fill-gold" : "text-ink-subtle"} />
                                ))}
                            </div>
                            <p className="text-ink-muted mt-3 text-sm leading-relaxed">{t.content}</p>
                        </div>
                    ))}
                </div>
            )}

            {showForm && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
                    <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-surface border border-line rounded-2xl max-w-lg w-full p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="font-display text-2xl">Нов отзив</h2>
                            <button type="button" onClick={() => setShowForm(false)} className="text-ink-muted hover:text-gold">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="space-y-4">
                            <label className="block bg-surface2 border border-line rounded-xl px-4 py-3">
                                <span className="block text-[10px] uppercase tracking-[0.25em] text-ink-subtle">Автор</span>
                                <input required value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="w-full mt-1 bg-transparent text-sm focus:outline-none" data-testid="testimonial-author" />
                            </label>
                            <label className="block bg-surface2 border border-line rounded-xl px-4 py-3">
                                <span className="block text-[10px] uppercase tracking-[0.25em] text-ink-subtle">Локация</span>
                                <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full mt-1 bg-transparent text-sm focus:outline-none" data-testid="testimonial-location" />
                            </label>
                            <label className="block bg-surface2 border border-line rounded-xl px-4 py-3">
                                <span className="block text-[10px] uppercase tracking-[0.25em] text-ink-subtle">Рейтинг (1–5)</span>
                                <input type="number" min={1} max={5} value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} className="w-full mt-1 bg-transparent text-sm focus:outline-none" data-testid="testimonial-rating" />
                            </label>
                            <label className="block bg-surface2 border border-line rounded-xl px-4 py-3">
                                <span className="block text-[10px] uppercase tracking-[0.25em] text-ink-subtle">Съдържание</span>
                                <textarea required rows={4} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="w-full mt-2 bg-transparent text-sm focus:outline-none resize-none" data-testid="testimonial-content" />
                            </label>
                        </div>
                        {error && <div className="mt-4 text-sm text-red-400">{error}</div>}
                        <div className="mt-6 flex justify-end gap-3">
                            <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2 border border-line rounded-full text-sm">Отказ</button>
                            <button type="submit" className="px-6 py-2 bg-gold text-bg rounded-full text-sm font-medium" data-testid="testimonial-submit">Запази</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
